import type { SnakeVisualConfig } from "./gameplay-config";

export interface SnakeHeadGeometryPoint {
  readonly x: number;
  readonly y: number;
}

export interface SnakeHeadGeometryDirection {
  readonly forwardX: number;
  readonly forwardY: number;
  readonly sideX: number;
  readonly sideY: number;
}

interface SnakeHeadProfilePoint {
  readonly forwardRatio: number;
  readonly sideRatio: number;
}

const upperProfile: readonly SnakeHeadProfilePoint[] = [
  { forwardRatio: 0.924, sideRatio: 0.237 },
  { forwardRatio: 0.864, sideRatio: 0.447 },
  { forwardRatio: 0.667, sideRatio: 0.724 },
  { forwardRatio: 0.326, sideRatio: 0.934 },
  { forwardRatio: -0.038, sideRatio: 1 },
  { forwardRatio: -0.295, sideRatio: 0.895 },
  { forwardRatio: -0.47, sideRatio: 0.711 },
];

const projectPoint = (
  position: SnakeHeadGeometryPoint,
  radius: number,
  direction: SnakeHeadGeometryDirection,
  visual: Pick<SnakeVisualConfig, "headLengthScale" | "headWidthScale">,
  profile: SnakeHeadProfilePoint,
): SnakeHeadGeometryPoint => ({
  x:
    position.x +
    direction.forwardX *
      radius *
      visual.headLengthScale *
      profile.forwardRatio +
    direction.sideX * radius * visual.headWidthScale * profile.sideRatio,
  y:
    position.y +
    direction.forwardY *
      radius *
      visual.headLengthScale *
      profile.forwardRatio +
    direction.sideY * radius * visual.headWidthScale * profile.sideRatio,
});

export const createSnakeHeadOutline = (
  position: SnakeHeadGeometryPoint,
  radius: number,
  direction: SnakeHeadGeometryDirection,
  visual: Pick<SnakeVisualConfig, "headLengthScale" | "headWidthScale">,
): readonly [SnakeHeadGeometryPoint, ...SnakeHeadGeometryPoint[]] => {
  const upper = upperProfile.map((profile) =>
    projectPoint(position, radius, direction, visual, profile),
  );
  const tip = projectPoint(position, radius, direction, visual, {
    forwardRatio: 1,
    sideRatio: 0,
  });
  const lower = upperProfile.map((profile) =>
    projectPoint(position, radius, direction, visual, {
      forwardRatio: profile.forwardRatio,
      sideRatio: -profile.sideRatio,
    }),
  );
  return [...upper.reverse(), tip, ...lower] as [
    SnakeHeadGeometryPoint,
    ...SnakeHeadGeometryPoint[],
  ];
};

export const projectSnakeHeadFeature = (
  position: SnakeHeadGeometryPoint,
  radius: number,
  direction: SnakeHeadGeometryDirection,
  forwardScale: number,
  sideScale: number,
): SnakeHeadGeometryPoint => ({
  x:
    position.x +
    direction.forwardX * radius * forwardScale +
    direction.sideX * radius * sideScale,
  y:
    position.y +
    direction.forwardY * radius * forwardScale +
    direction.sideY * radius * sideScale,
});
